/**
 * TableAutocomplete - Custom Autocomplete for table inline editing
 *
 * This is a copy of the base Autocomplete component with table-specific modifications:
 * 1. Adds `sortSelectedFirst` prop to sort options (selected first, then alphabetically)
 * 2. Removes the hardcoded onKeyDown that blocks Enter key propagation
 * 3. Adds `selectedValues` prop to track current selection for sorting
 *
 * Use this component instead of the base Autocomplete for table inline editing.
 */

import {
  type ChipTypeMap,
  type FilterOptionsState,
  type AutocompleteChangeDetails,
  type AutocompleteValue,
  Box,
  createFilterOptions,
  Autocomplete as MuiAutocomplete,
  useTheme,
} from '@mui/material';
import { AutocompleteProps, ComboBoxOption } from 'components/inputs/types';
import { t } from '@lingui/macro';
import React, { useState, useCallback, useMemo, useRef, memo } from 'react';
import { Paper } from '@mui/material';
import { parseSearch } from 'components/inputs/ComboBox';
import { useGet } from 'lib/api';
import { debounce, omit } from 'lodash';
import { TextInput, TextInputProps } from 'components/inputs/TextInput';
import { colors } from 'styles/theme/colors';
import { Check } from '@phosphor-icons/react/dist/ssr';
import { CaretDown, Trash, Warning } from '@phosphor-icons/react';
import { Chip } from 'components/chip';

/**
 * Additional props for TableAutocomplete
 */
type TableAutocompleteExtraProps<Value> = {
  /** When true, sorts options with selected items first, then alphabetically */
  sortSelectedFirst?: boolean;
  /** Currently selected values - used for sorting when sortSelectedFirst is true */
  selectedValues?: Value[];
  /** Optional "Clear" option to prepend when showClearOption is true (e.g. multiselect clear-all in dropdown) */
  clearOption?: Value;
  /** When true and clearOption is set and multiple, prepend clearOption to the options list */
  showClearOption?: boolean;
  /** Callback fired when the "Set empty" footer is clicked */
  onSetEmpty?: () => void;
};

const TableAutocompleteInner = <
  Value extends Omit<ComboBoxOption, 'handleClick'>,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
>({
  sx,
  options = [],
  openText = t`Open`,
  loadingText = t`Loading...`,
  noOptionsText = t`No options`,
  variant = 'unstyled',
  api = null,
  optionsLimit,
  atSearch = false,
  filterOptions: filterOptionsProps,
  onChange,
  onBlur,
  componentsProps,
  optionsChipRender,
  optionsChipProps,
  isInvalidOption,
  sortSelectedFirst = false,
  selectedValues,
  clearOption,
  showClearOption = false,
  onSetEmpty,
  allowEmptySearchFetch = false,
  ...rest
}: Omit<
  AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>,
  'inputVariant' | 'options'
> &
  TableAutocompleteExtraProps<Value> & {
    options?: Value[];
    name?: string;
    required?: boolean;
    variant?: AutocompleteProps<
      Value,
      Multiple,
      DisableClearable,
      FreeSolo,
      ChipComponent
    >['inputVariant'];
  }) => {
  const [search, setSearch] = useState('');
  const theme = useTheme();

  // Helper to resolve vars (can be array or function)
  const getVars = useCallback(() => {
    if (!api?.vars) return [];
    return typeof api.vars === 'function' ? api.vars() : api.vars;
  }, [api?.vars]);

  const { data } = useGet(
    api?.url || null,
    api?.search ? [api?.key, search] : api?.key || '',
    api?.search ? [parseSearch(atSearch, search), ...getVars()] : [...getVars()],
    undefined,
    api?.revalidate !== false,
    {
      enabled: (() => {
        if (!api) return false;
        if (api.search) {
          if (allowEmptySearchFetch) {
            return typeof api.vars !== 'function' || getVars().length > 0;
          }
          return Boolean(parseSearch(atSearch, search));
        }
        return true;
      })(),
      ...(api?.options ?? {}),
    }
  );
  const selectOptions = (data && api ? api.responseTransform(data) : options) as Value[];

  // Sort options: selected first, then alphabetically
  const sortedSelectOptions = useMemo(() => {
    if (!sortSelectedFirst || !selectedValues?.length) return selectOptions;
    const selectedIds = new Set(selectedValues.map(v => String(v.id)));
    return [...selectOptions].sort((a, b) => {
      const aSelected = selectedIds.has(String(a.id));
      const bSelected = selectedIds.has(String(b.id));
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return (a.label ?? '').localeCompare(b.label ?? '');
    });
  }, [selectOptions, selectedValues, sortSelectedFirst]);

  const optionsWithClear = sortedSelectOptions;

  const onSetEmptyRef = useRef(onSetEmpty);
  onSetEmptyRef.current = onSetEmpty;

  const SetEmptyPaper = useMemo(() => {
    if (!showClearOption || !clearOption) return undefined;
    const label = clearOption.label;
    return function SetEmptyPaperComponent(paperProps: React.HTMLAttributes<HTMLElement>) {
      return (
        <Paper {...paperProps} elevation={8}>
          {paperProps.children}
          <Box
            onMouseDown={e => {
              e.preventDefault();
              e.stopPropagation();
              onSetEmptyRef.current?.();
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              borderTop: '0.5px solid',
              borderColor: 'border.primarySubtle',
              color: 'text.error',
              cursor: 'pointer',
              fontSize: '0.875rem',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            <Trash size={20} color={colors.error[400]} />
            <span>{label}</span>
          </Box>
        </Paper>
      );
    };
  }, [showClearOption, clearOption]);

  // Use useRef to create a stable debounced function that persists across renders
  const debouncedSetSearchRef = useRef(
    debounce((newInputValue: string) => {
      setSearch(newInputValue);
    }, 250)
  );

  // Stable callback that uses the ref
  const inputChangeApi = useCallback((newInputValue: string) => {
    debouncedSetSearchRef.current(newInputValue);
  }, []);

  // limit amount of options shown
  const defaultFilterOptions = createFilterOptions<Value>();
  const filterOptions = (opts: Value[], state: FilterOptionsState<Value>) => {
    if (atSearch) {
      const searchValue = parseSearch(atSearch, state.inputValue);
      if (!searchValue) return [];
      return defaultFilterOptions(opts, { ...state, inputValue: searchValue }).slice(
        0,
        optionsLimit
      );
    }
    return defaultFilterOptions(opts, state).slice(0, optionsLimit);
  };

  return (
    <MuiAutocomplete
      selectOnFocus
      openText={openText}
      loadingText={loadingText}
      {...(SetEmptyPaper ? { PaperComponent: SetEmptyPaper } : {})}
      // NOTE: Removed the hardcoded onKeyDown that blocked Enter key propagation
      // This allows parent components to handle Enter key events via useEnterToClose hook
      noOptionsText={noOptionsText}
      options={(optionsWithClear ?? []) as Value[]}
      sx={{
        '.MuiInputBase-root': {
          padding: 0,
          input: { padding: '10px 14px' },
        },
        '.MuiFormControl-root': {
          '.MuiInputBase-root': {
            input: { padding: '10px 14px' },
          },
        },
        flex: 1,
        ...sx,
      }}
      filterOptions={
        optionsLimit
          ? (filterOptions as (
              filteredOptions: Value[],
              state: FilterOptionsState<Value>
            ) => Value[])
          : filterOptionsProps
      }
      onInputChange={(event, newInputValue, reason) => {
        rest?.onInputChange?.(event, newInputValue, reason);
        if (api?.search) inputChangeApi(newInputValue);
      }}
      renderOption={(optionProps, option) => {
        const isInvalid = isInvalidOption?.(option);
        const textColor = isInvalid ? theme.palette.text.disabled : theme.palette.text.secondary;
        return (
          <Box
            component="li"
            {...optionProps}
            sx={{
              display: 'flex',
              width: '100%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flexDirection: 'row',
              padding: '8px 10px',
              gap: '8px',
              marginBlock: '1px',
              borderRadius: '4px',
              color: `${textColor} !important`,
              fill: `${textColor} !important`,
              '& span, & div': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
              ...(isInvalid ? { '& .MuiAvatar-root': { opacity: 0.5 } } : {}),
              '&.MuiAutocomplete-option[aria-selected="true"]': {
                background: colors.brand[50],
                fontWeight: 600,
                '& span, & div': {
                  fontWeight: 600,
                },
              },
            }}
            key={option?.id}
          >
            {isInvalid && (
              <Warning color="inherit" size={24} style={{ minWidth: '24px', minHeight: '24px' }} />
            )}
            {option.CustomElement ? (
              <option.CustomElement label={option.label} {...option.customProps} />
            ) : (
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {option.label}
              </span>
            )}
            {(optionsChipRender || optionsChipProps?.label) && (
              <Chip
                isTooltip={false}
                size="sm"
                label={optionsChipRender?.(option)}
                {...omit(optionsChipProps, ['labelProps'])}
                sx={themeSX => {
                  return {
                    marginLeft: 'auto',
                    ...optionsChipProps?.sxWithOption?.(themeSX, option),
                  };
                }}
              />
            )}
            {optionProps?.['aria-selected'] && (
              <Check
                size={20}
                style={{
                  minWidth: '20px',
                  minHeight: '20px',
                  ...(!(optionsChipRender || optionsChipProps?.label)
                    ? { marginLeft: 'auto' }
                    : {}),
                }}
                color={colors.brand[400]}
              />
            )}
          </Box>
        );
      }}
      popupIcon={<CaretDown size={20} />}
      {...rest}
      componentsProps={{
        ...componentsProps,
        paper: {
          ...(componentsProps?.paper ?? {}),
          sx: {
            ...((sortedSelectOptions?.length > 0 || !rest?.freeSolo) && {
              marginTop: '8px',
              minWidth: 250,
              borderRadius: '8px',
              border: `0.5px solid ${colors.grey[200]}`,
              boxShadow:
                '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
              ul: {
                padding: '6px',
              },
            }),
            ...(componentsProps?.paper?.sx ?? {}),
          },
        },
      }}
      onChange={(event, value, reason, details) => {
        onChange?.(
          event,
          (value ?? '') as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>,
          reason,
          details as AutocompleteChangeDetails<Value>
        );
      }}
      onBlur={event => {
        onBlur?.(event);
      }}
      renderInput={params => {
        const textParams = {
          ...params,
          InputLabelProps: { ...params.InputLabelProps, shrink: true, required: rest.required },
        };
        return (
          <TextInput
            {...(omit(rest, [
              'value',
              'name',
              'onBlur',
              'onChange',
              'data-testid',
            ]) as TextInputProps)}
            {...textParams}
            variant={variant}
            InputProps={{ ...params.InputProps, ...(rest.InputProps ?? {}) }}
          />
        );
      }}
    />
  );
};

// Wrap with memo for performance optimization
export const TableAutocomplete = memo(TableAutocompleteInner) as typeof TableAutocompleteInner;
